import { Button } from '@understory-io/leaf/button';
import { IconButton } from '@understory-io/leaf/icon-button';
import { LinkButton } from '@understory-io/leaf/link-button';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { cx } from '~/styled-system/css';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  useNavigate,
  useNavigation,
  useSearchParams,
  useSubmit,
} from 'react-router';
import { EventCard } from '~/ui/event-card/event-card';
import type { EventCardSession } from '~/ui/event-card/multi-session';
import type { SeatCounterVisibility } from '~/ui/availability-label/availability-label';
import { FormattedPrice } from '~/ui/formatted';
import { AvailabilityCalendar } from '~/ui/availability-calendar';
import { useAvailability } from '~/ui/availability-calendar/use-availability';
import type {
  EventAvailability,
  FilterOptions,
} from '~/domain/availability/index.server';
import type { LocationAddress } from '~/domain/availability/locations.server';
import { useLocaleHref, localeHrefExternal } from '~/lib/use-locale-href';
import { ampli } from '~/domain/plugins/analytics';
import { useWaitlistTracking } from '~/domain/plugins/services/waitlist-tracking';
import { FilterIcon } from '~/ui/icons/filter';
import { FilterPicker } from '~/ui/filter-picker';
import { WaitlistDialog } from '~/ui/waitlist';
import { AvailabilityEmptyState } from '~/ui/availability-empty-state';
import { focusVisibleRing } from '~/ui/app-shell/shared';
import { BookingPanelCard } from './booking-panel-card';
import {
  trackPanelDateSelected,
  trackPanelEventSelected,
  trackPanelWaitlistOpened,
} from '../tracking';

export type BookingPanelEvent = {
  eventId: string;
  startTime: string;
  endTime?: string | null;
  location?: LocationAddress | null;
  locales: string[];
  status: EventAvailability['status'];
  availableSeats: number;
  hideSeatCount: boolean;
  bookingMaxBelowSeatCount: boolean;
  /** The host's own text for this date (e.g. a theme), if any. */
  description: string | null;
  /** Sessions on this event. Length 1 for single-session experiences, 2+
   *  for multi-session courses. */
  sessions: EventCardSession[];
};

type BookingPanelScheduledProps = {
  experienceId: string;
  /** Bare checkout-entry URL for this experience, built server-side so it is
   *  cross-domain (the owner's storefront) for shared/connected experiences and
   *  same-domain otherwise. Used as-is as the `href` of the "See more dates"
   *  primary LinkButton — it's a plain external anchor, so do NOT route it
   *  through a react-router Link / `useLocaleHref` (that would fail to leave
   *  the current origin for cross-domain owners). */
  checkoutHref: string;
  /** Whether the price carries the "From" prefix — the storefront's
   *  `showFromPrice` preference. Controls only the prefix; the price itself
   *  renders whenever it is available. */
  showFromPrice: boolean;
  defaultPrice?: {
    amountCents: number;
    vatExclusiveAmountCents: number;
    currency: string;
    exponent: number;
    vatExempt: boolean;
  } | null;
  events: BookingPanelEvent[];
  /**
   * Server-decided render mode. The panel is otherwise stateless:
   *  - `'calendar'`  – availability calendar; first event date is
   *                    auto-selected so Book now is enabled on first paint.
   *  - `'list'`      – EventCard list capped at `LIST_DISPLAY_LIMIT`; a slot
   *                    click continues straight into the booking dialog. The
   *                    "See more dates" button renders below the list when the
   *                    loader returned more than the cap.
   *  - `'empty'`     – bell-icon waitlist panel with an optional "Or
   *                    request an event" link.
   *
   * `'calendar'` and `'list'` both carry the "Get notified on new dates" link
   * at the bottom (opens the waitlist drawer) — `'empty'` has its own waitlist,
   * so it opts out.
   */
  mode?: 'calendar' | 'list' | 'empty';
  /** First upcoming event date (YYYY-MM-DD). Anchors the calendar focus
   *  AND auto-selects the date in calendar mode. */
  initialFocusDate?: string | null;
  /** Available filter values (languages + locations). The filter button
   *  hides entirely when both have <=1 entry. */
  filterOptions?: FilterOptions;
  seatCounterVisibility?: SeatCounterVisibility;
  /** Storefront preference: enables an "Or request an event" link inside
   *  the empty-state waitlist panel. Driven by storefronts-core. */
  privateEventRequestEnabled?: boolean;
  /** URL `:locale?` segment used to build the legacy `/request` link in the
   *  empty-state panel. Passed from the route so the link preserves the
   *  segment the visitor is currently on. */
  locale?: string;
  /** When false, checkout isn't reachable (the host cannot receive payments
   *  yet and we're not in preview) — the card shows PaymentsNotReadyState
   *  instead of the picker/CTA. Takes precedence over empty/list/calendar
   *  (mirrors the legacy NO_PAYMENT_METHODS precedence). */
  canProceedToCheckout?: boolean;
};

/** Max events shown in list mode. Beyond this, the panel surfaces
 *  "See more dates" so the visitor lands on the full checkout calendar. */
const LIST_DISPLAY_LIMIT = 3;

/**
 * The tablet+ sidebar for an experience that sells **scheduled events**: the
 * rich, interactive picker (calendar / EventCard list / empty-state waitlist +
 * filters) whose "Book now" opens the booking dialog.
 *
 * Experiences that sell open time slots (operating hours) have no events to pick
 * from and get their own sibling panel, `<BookingPanelOpenTimeSlot>` — the route
 * mounts one or the other. The mobile equivalent of both is the separate, far
 * simpler `<BookingBar>` floating button — the trees diverge enough (interactive
 * picker vs. a single CTA, dialog vs. direct-to-checkout) that they're split
 * rather than display-toggled inside one component.
 */
export function BookingPanelScheduled({
  experienceId,
  checkoutHref,
  showFromPrice,
  defaultPrice,
  events,
  mode = 'list',
  initialFocusDate,
  filterOptions,
  seatCounterVisibility = 'limitedOnly',
  privateEventRequestEnabled = false,
  locale,
  canProceedToCheckout = true,
}: BookingPanelScheduledProps) {
  const intl = useIntl();
  const localeHref = useLocaleHref();
  const navigate = useNavigate();
  const submit = useSubmit();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  // The filter dialog applies via `useSubmit({ method: 'get' })`, which
  // puts the route into a 'loading' navigation state until the loader
  // finishes. Use that signal to dim the panel body so the visitor knows
  // the dots / event list they're looking at are about to change.
  const isRevalidating = navigation.state === 'loading';

  const filters = useMemo(() => {
    const languages = searchParams.getAll('language');
    const locationIds = searchParams.getAll('locationId');
    return {
      languages: languages.length > 0 ? languages : undefined,
      locationIds: locationIds.length > 0 ? locationIds : undefined,
    };
  }, [searchParams]);

  // Reset key for local selection state — when URL filters change OR the
  // loader switches mode (e.g. filtering down to a single date flips
  // calendar→list), reset the local selection so it stays consistent with
  // the new payload.
  const filtersKey = useMemo(() => {
    const languages = filters.languages?.slice().sort().join(',') ?? '';
    const locationIds = filters.locationIds?.slice().sort().join(',') ?? '';
    return `${languages}|${locationIds}`;
  }, [filters]);

  // Calendar mode: auto-select the first event date so Book now is enabled
  // on first paint. The visitor can click another date to override.
  const [selectedDate, setSelectedDate] = useState<string | null>(
    initialFocusDate ?? null
  );
  useEffect(() => {
    setSelectedDate(initialFocusDate ?? null);
  }, [filtersKey, mode, initialFocusDate]);

  // List mode: no timeslot is preselected. Clicking a slot selects it and
  // immediately continues into the booking dialog (see handleEventSelect), so
  // there's no Book now button to keep enabled. The highlight is reset when
  // the filters or mode change so it tracks whatever the loader returned.
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  // The waitlist drawer (opened from the "Get notified on new dates" link at
  // the bottom of the picker). Closed on filter/mode changes so a stale open
  // form doesn't linger over a freshly-loaded date set.
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  useEffect(() => {
    setSelectedEventId(null);
    setWaitlistOpen(false);
  }, [filtersKey, mode]);

  const emptyStateTracking = useWaitlistTracking({
    experienceId,
    surface: 'experience_empty_state',
  });
  const waitlistDialogTracking = useWaitlistTracking({
    experienceId,
    surface: 'experience_dialog',
  });

  // Filter dialog. Hidden entirely when the experience has only one option
  // in both axes (no choice to make) OR in empty mode where there's nothing
  // for the filter to narrow down.
  const [filterOpen, setFilterOpen] = useState(false);
  const hasFilterableLanguages = (filterOptions?.languages.length ?? 0) > 1;
  const hasFilterableLocations = (filterOptions?.locations.length ?? 0) > 1;

  const showFilterButton =
    mode !== 'empty' &&
    filterOptions != null &&
    (hasFilterableLanguages || hasFilterableLocations);
  const activeFilterCount =
    (filters.languages?.length ?? 0) + (filters.locationIds?.length ?? 0);

  // The calendar's day statuses answer the same question the event list does,
  // so they narrow by exactly what the visitor filtered on and nothing else.
  // No location is in effect until one is picked: scoping the dots to a
  // location the visitor never chose would dot days as sold out that are
  // bookable somewhere else, with the list behind them disagreeing.
  const availabilityFilters = useMemo(
    () => ({
      languages: filters.languages,
      locationIds: filters.locationIds,
    }),
    [filters.languages, filters.locationIds]
  );

  // Active filters matched no events, but the experience itself still has
  // upcoming dates. The server decides `mode` from the canonical (unfiltered)
  // events, so `mode !== 'empty'` guarantees dates exist — an empty filtered
  // `events` set with filters active therefore means "your filter is too
  // narrow", never "nothing is on". Surfaced as a dedicated state with a
  // "Show all dates" affordance so the visitor is never stranded on a blank
  // calendar (US-10774).
  const filtersMatchedNothing =
    mode !== 'empty' && activeFilterCount > 0 && events.length === 0;

  /** Clears the language/location filters by re-submitting the current URL
   *  with those params removed — same GET-submit mechanism the FilterPicker
   *  uses, so the loader refetches the unfiltered dates. */
  function handleClearFilters() {
    const params = new URLSearchParams(searchParams);
    params.delete('language');
    params.delete('locationId');
    void submit(params, { method: 'get', preventScrollReset: true });
  }

  // Pre-warm the calendar availability cache — keyed by (experienceId,
  // filters), so list/empty mode warms the very entry the booking dialog's date
  // stepper reads on open.
  const { availability, readyMonths, failedMonths, retry, handleMonthChange } =
    useAvailability({
      experienceId,
      initialFocusDate,
      filters: availabilityFilters,
      // The empty state has no calendar to fill.
      enabled: mode !== 'empty',
    });

  // `/request` is owned by the legacy Next.js app and not in the typesafe
  // route map, so reach for `localeHrefExternal` instead of `localeHref`.
  const requestEventHref = localeHrefExternal(locale, '/request');

  const bookingDialogHref = localeHref(
    '/:locale?/experience/:experienceId/book',
    { experienceId }
  );

  /** Booking-dialog URL carrying the active filters (already in the page URL)
   *  plus the given params — `from` anchors the date, `eventId` pre-selects a
   *  list event. The single builder is what keeps a slot click and the Book now
   *  CTA that stands in for it landing on the exact same target. */
  function bookingDialogHrefWith(extra: Record<string, string>) {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(extra)) params.set(key, value);
    const qs = params.toString();
    return qs ? `${bookingDialogHref}?${qs}` : bookingDialogHref;
  }

  /** Opens the booking-dialog modal route over the current page. Never resets
   *  scroll: the dialog renders over this page via the parent's Outlet, so the
   *  page must not jump back to the top. */
  function openBookingDialog(extra: Record<string, string>) {
    void navigate(bookingDialogHrefWith(extra), { preventScrollReset: true });
  }

  /** Calendar mode: clicking a date selects it and opens the on-page booking
   *  dialog. */
  function handleDateSelect(date: string) {
    trackPanelDateSelected({ experienceId, widgetMode: mode, date });
    setSelectedDate(date);
    openBookingDialog({ from: date });
  }

  /** List mode: clicking a time-slot marks it selected and continues straight
   *  into the dialog (with more than one date on offer, the click *is* the
   *  choice, so there's no separate Book now to press). */
  function handleEventSelect(eventId: string, positionInList: number) {
    setSelectedEventId(eventId);
    const event = events.find((e) => e.eventId === eventId);
    if (event) {
      trackPanelEventSelected({
        experienceId,
        widgetMode: mode,
        positionInList,
        startTime: event.startTime,
      });
    }
    openBookingDialog(event ? eventParams(event) : { eventId });
  }

  const formattedDefaultPrice = defaultPrice ? (
    <FormattedPrice
      price={{
        vatInclusivePriceCents: defaultPrice.amountCents,
        vatExclusivePriceCents: defaultPrice.vatExclusiveAmountCents,
        currency: defaultPrice.currency,
        exponent: defaultPrice.exponent,
        vatExempt: defaultPrice.vatExempt,
      }}
    />
  ) : null;

  // List-mode display window. The loader fetches enough events to apply the
  // calendar-vs-list rule; trim to LIST_DISPLAY_LIMIT for the visible card
  // list. `hasMoreEvents` decides between the "See more dates" button and the
  // waitlist text-link below the list.
  const displayedEvents = useMemo(
    () => (mode === 'list' ? events.slice(0, LIST_DISPLAY_LIMIT) : []),
    [mode, events]
  );
  const hasMoreEvents = mode === 'list' && events.length > LIST_DISPLAY_LIMIT;

  // The one-off widget: list mode holding a single event (US-10453). With no
  // date to choose between, the lone slot click is a poor primary affordance —
  // the design gives this shape a Book now CTA of its own, pointed at that one
  // event. Any other list length keeps the click-to-continue behaviour.
  const singleEvent =
    mode === 'list' && displayedEvents.length === 1
      ? displayedEvents[0]!
      : null;

  // Book now renders for the calendar (pick a date, then commit) and for the
  // one-off widget (nothing to pick, so commit straight to its only event).
  // Every other list length continues from the time-slot click instead, and the
  // empty state's waitlist panel is its own primary affordance.
  const showBookNow =
    !filtersMatchedNothing && (mode === 'calendar' || singleEvent !== null);

  // The waitlist link sits at the bottom of every picker that actually shows
  // dates — calendar and list alike — so a guest can opt in even when dates are
  // available (US-11360). The empty state carries its own waitlist, and the
  // filtered-empty state offers "Show all dates" instead, so both opt out.
  const showWaitlistLink = !filtersMatchedNothing && mode !== 'empty';

  // Same target the slot click produces, so the two ways into the one-off
  // widget's dialog can't drift; in calendar mode it's the date the visitor
  // selected. Plain computation (not `useMemo`): it's a couple of
  // `URLSearchParams` calls on a short string, and this shape is what React
  // Compiler can actually preserve — a memo here trips
  // `react-hooks/preserve-manual-memoization` because `bookingDialogHrefWith`
  // is also called from the non-memoized `openBookingDialog` closure.
  const bookNowDialogHref = singleEvent
    ? bookingDialogHrefWith(eventParams(singleEvent))
    : bookingDialogHrefWith(selectedDate ? { from: selectedDate } : {});

  // Calendar mode: until the visitor has picked a date. One-off widget: when
  // its only event can't be booked — the EventCard disables itself on exactly
  // these statuses, and Book now is just a second way to press it.
  const bookDisabled = singleEvent
    ? singleEvent.status === 'soldOut' || singleEvent.status === 'unavailable'
    : !selectedDate;

  const filterButton =
    showFilterButton && filterOptions ? (
      <IconButton
        appearance="text"
        size="standard"
        onClick={() => setFilterOpen(true)}
        aria-label={
          activeFilterCount > 0
            ? intl.formatMessage(
                {
                  defaultMessage: 'Filters ({count} active)',
                  description:
                    'Accessible label on the BookingWidget filter button when filters are applied',
                },
                { count: activeFilterCount }
              )
            : intl.formatMessage({
                defaultMessage: 'Filters',
                description:
                  'Accessible label on the BookingWidget filter button when no filters are applied',
              })
        }
        className={filterButtonStyles}
      >
        <FilterIcon size="large-inset" />
        {activeFilterCount > 0 && (
          <span aria-hidden="true" className={filterActiveDotStyles} />
        )}
      </IconButton>
    ) : null;

  return (
    <>
      <BookingPanelCard canProceedToCheckout={canProceedToCheckout}>
        {/* Two groups pushed apart: what the visitor reads and picks from sits
            at the top, what commits them sits at the bottom. Filling the card's
            min-height this way is what puts the slack under the time slot
            rather than stranding it below the CTA (US-10453). */}
        <Stack
          gap="medium"
          justify="space-between"
          className={css({ flexGrow: '1' })}
        >
          <Stack gap="medium">
            {formattedDefaultPrice || filterButton !== null ? (
              <Stack
                direction="row"
                align="center"
                justify="space-between"
                gap="small"
                className={css({
                  paddingLeft: 'small',
                })}
              >
                {formattedDefaultPrice ? (
                  <Text
                    as="p"
                    style="body-small-regular"
                    color="tertiary"
                    className={priceLabelStyles}
                  >
                    {showFromPrice ? (
                      <FormattedMessage
                        defaultMessage="<bold>From {price}</bold> / person"
                        description="Default price label on the experience details booking widget, with 'From' prefix"
                        values={{
                          bold: (chunks) => (
                            <Text
                              as="span"
                              style="subheadline-small"
                              color="primary"
                            >
                              {chunks}
                            </Text>
                          ),
                          price: formattedDefaultPrice,
                        }}
                      />
                    ) : (
                      <FormattedMessage
                        defaultMessage="<bold>{price}</bold> / person"
                        description="Default price label on the experience details booking widget, without 'From' prefix"
                        values={{
                          bold: (chunks) => (
                            <Text
                              as="span"
                              style="subheadline-small"
                              color="primary"
                            >
                              {chunks}
                            </Text>
                          ),
                          price: formattedDefaultPrice,
                        }}
                      />
                    )}
                  </Text>
                ) : (
                  <span />
                )}

                <Stack
                  direction="row"
                  align="center"
                  gap="xsmall"
                  className={headerControlsStyles}
                >
                  {filterButton}
                </Stack>
              </Stack>
            ) : null}
            <div
              aria-busy={isRevalidating || undefined}
              className={cx(isRevalidating && revalidationPendingStyles)}
            >
              {filtersMatchedNothing ? (
                // Active filters (e.g. a language) matched no events, though the
                // experience still has upcoming dates in other languages /
                // locations. Rather than a blank calendar, show why and offer a
                // one-tap way back to every date (US-10774).
                <Stack
                  gap="medium"
                  align="center"
                  className={css({
                    textAlign: 'center',
                    paddingBlock: 'medium',
                  })}
                >
                  <Stack gap="xsmall" align="center">
                    <Text as="p" style="body-large-bold">
                      {(filters.locationIds?.length ?? 0) === 0 ? (
                        <FormattedMessage
                          defaultMessage="No available dates for the selected {count, plural, one {language} other {languages}}"
                          description="Booking widget heading when the selected language filter(s) match no events"
                          values={{ count: filters.languages?.length ?? 0 }}
                        />
                      ) : (filters.languages?.length ?? 0) === 0 ? (
                        <FormattedMessage
                          defaultMessage="No available dates for the selected {count, plural, one {location} other {locations}}"
                          description="Booking widget heading when the selected location filter(s) match no events"
                          values={{ count: filters.locationIds?.length ?? 0 }}
                        />
                      ) : (
                        <FormattedMessage
                          defaultMessage="No available dates for the selected filters"
                          description="Booking widget heading when the active language and location filters together match no events"
                        />
                      )}
                    </Text>
                    <Text
                      as="p"
                      style="body-small-regular"
                      className={css({ color: 'content.secondary' })}
                    >
                      <FormattedMessage
                        defaultMessage="This experience has other dates available."
                        description="Booking widget subtitle when the active filters match no events"
                      />
                    </Text>
                  </Stack>
                  <Button
                    appearance="secondary"
                    size="small"
                    onClick={handleClearFilters}
                  >
                    <FormattedMessage
                      defaultMessage="Show all dates"
                      description="Booking widget button that clears the filters and reveals all available dates"
                    />
                  </Button>
                </Stack>
              ) : (
                <>
                  {mode === 'calendar' && (
                    <div className={css({ paddingBottom: 'small' })}>
                      <AvailabilityCalendar
                        selectedDate={selectedDate}
                        availability={availability}
                        readyMonths={readyMonths}
                        failedMonths={failedMonths}
                        onRetry={retry}
                        onDateSelect={handleDateSelect}
                        onMonthChange={handleMonthChange}
                        initialFocusDate={initialFocusDate}
                      />
                    </div>
                  )}

                  {mode === 'list' && displayedEvents.length > 0 && (
                    <Stack
                      as="ol"
                      // Safari VoiceOver strips list semantics when list-style
                      // is removed; the explicit role restores them.
                      role="list"
                      gap="small"
                      className={listStyles}
                    >
                      {displayedEvents.map((event, index) => (
                        <li key={event.eventId}>
                          <EventCard
                            sessions={event.sessions}
                            description={event.description}
                            clampDescription
                            locales={event.locales}
                            status={event.status}
                            availableSeats={event.availableSeats}
                            hideSeatCount={event.hideSeatCount}
                            bookingMaxBelowSeatCount={
                              event.bookingMaxBelowSeatCount
                            }
                            selected={selectedEventId === event.eventId}
                            onClick={() =>
                              handleEventSelect(event.eventId, index)
                            }
                            seatCounterVisibility={seatCounterVisibility}
                          />
                        </li>
                      ))}
                    </Stack>
                  )}

                  {mode === 'empty' && (
                    <AvailabilityEmptyState
                      requestEventHref={
                        privateEventRequestEnabled
                          ? requestEventHref
                          : undefined
                      }
                      {...emptyStateTracking}
                    />
                  )}
                </>
              )}
            </div>
          </Stack>

          {/* Omitted only when nothing would sit here: the empty state and
                the filtered-empty state carry their own affordances. */}
          {(showWaitlistLink || showBookNow) && (
            <Stack gap="medium">
              {/* Primary "See more dates" button when the loader's window holds
                more events than the panel shows — opens the full checkout
                calendar. */}
              {hasMoreEvents && (
                <LinkButton
                  href={checkoutHref}
                  appearance="primary"
                  size="large"
                >
                  <FormattedMessage
                    defaultMessage="See more dates"
                    description="Primary button below the booking widget time-slot list that opens the full checkout calendar"
                  />
                </LinkButton>
              )}
              {/* Opens the booking-dialog modal route, which renders over this
                page via the parent's Outlet. See `showBookNow` for which shapes
                of the panel get this CTA at all. */}
              {showBookNow && (
                <LinkButton
                  href={bookNowDialogHref}
                  appearance="primary"
                  size="large"
                  disabled={bookDisabled}
                  preventScrollReset
                  onClick={() => {
                    // Not a `Checkout *` event, so the checkout layout's
                    // enrichment plugin never stamps it — `experience_id` has to
                    // ride along explicitly. This panel has no ticket picker of
                    // its own (that lives in the booking dialog this opens), so
                    // `ticket_count` is always 0.
                    ampli.experienceBookingPanelBookNowClicked({
                      experience_id: experienceId,
                      availability_mode: 'scheduled',
                      widget_mode: mode,
                      ticket_count: 0,
                    });
                  }}
                >
                  <FormattedMessage
                    defaultMessage="Book now"
                    description="Booking widget primary call-to-action that starts the checkout flow"
                  />
                </LinkButton>
              )}
              {/* Waitlist opt-in, at the bottom of the section — a guest can
                    ask to hear about new dates even when dates are on offer
                    (US-11360). Opens the signup form in a drawer / centered
                    modal. Bare link (no ?from), matching the former text-link. */}
              {showWaitlistLink && (
                <div className={belowListStyles}>
                  <button
                    type="button"
                    onClick={() => {
                      trackPanelWaitlistOpened({
                        experienceId,
                        widgetMode: mode,
                      });
                      setWaitlistOpen(true);
                    }}
                    className={cx(focusVisibleRing, waitlistTriggerStyles)}
                  >
                    <FormattedMessage
                      defaultMessage="Get notified on new dates"
                      description="Inline waitlist text button below the booking widget time-slot list"
                    />
                  </button>
                </div>
              )}
            </Stack>
          )}
        </Stack>
      </BookingPanelCard>

      {filterOpen && filterOptions && (
        <FilterPicker
          filterOptions={filterOptions}
          activeLanguages={filters.languages ?? []}
          activeLocations={filters.locationIds ?? []}
          onClose={() => setFilterOpen(false)}
        />
      )}

      {/* The waitlist signup, opened from the "Get notified on new dates" link.
          A bottom sheet on mobile / centered modal on tablet+; the panel itself
          only renders tablet+, so in practice this is the modal. */}
      <WaitlistDialog
        open={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        {...waitlistDialogTracking}
      />
    </>
  );
}

/** The booking-dialog params that pin the flow to one list event: `eventId`
 *  pre-selects it and `from` anchors the date stepper on its day. Shared by the
 *  slot click and the one-off widget's Book now so they stay one target. */
function eventParams(event: BookingPanelEvent): Record<string, string> {
  return { from: event.startTime.slice(0, 10), eventId: event.eventId };
}

const listStyles = css({
  listStyle: 'none',
  margin: 'none',
  padding: 'none',
});

const belowListStyles = css({
  display: 'flex',
  justifyContent: 'center',
});

// Underlined text button at the bottom of the picker that opens the waitlist
// drawer. Mirrors the former Waitlist "text-link" appearance so the affordance
// is unchanged — only what it opens (a drawer / modal) is new.
const waitlistTriggerStyles = css({
  textStyle: 'body-small-regular',
  border: 'none',
  padding: 'none',
  background: '[transparent]',
  cursor: 'interactive',
  textDecoration: 'underline',
  color: 'content.primary',
});

const revalidationPendingStyles = css({
  opacity: '[0.5]',
  pointerEvents: 'none',
});

// The price never wraps or shrinks — the location dropdown next to it absorbs
// any width pressure by ellipsizing instead.
const priceLabelStyles = css({
  whiteSpace: 'nowrap',
  flexShrink: '0',
});

// Right-hand header controls (location dropdown + filter button) shrink before
// the price does.
const headerControlsStyles = css({
  minWidth: '[0]',
  flexShrink: '1',
});

const filterButtonStyles = css({
  position: 'relative',
  color: 'content.secondary',
});

const filterActiveDotStyles = css({
  position: 'absolute',
  top: 'tiny',
  right: 'tiny',
  width: 'xsmall',
  height: 'xsmall',
  borderRadius: 'full',
  background: 'interactive.primary',
  pointerEvents: 'none',
});
